import {
  accountTypes,
  getAppSourceType,
  setTaxIDType,
  secondaryCommitMessage,
  fundingTypes,
  applicationStatusTypes
} from '../../shared/utils';
import {
  pollForSubmissionResponse,
  commitConsent,
  getEnablementStatus,
  loadUser,
  getUserExistingAgreements
} from '../../shared/loaders';
import { formatDate } from '../../shared/dateUtils';
import { merge, mergeTyped, withDefault } from '../../../core/util';

import { commitNewInvestmentsBYOP } from '../shared/loaders';
import { DataLoader } from '../../../core/types/data-loader';
import {
  StateStoreOrVoid,
  ApplicationState,
  PartialStateStore
} from '../../../core/types/application-state';
import { IndividualApplicationService } from '../../shared/services/mutualfund-taxable-individual';
import {
  analyticsDataLayer,
  dispatchPageLoad
} from '../../../analytics/analytics-helper';
import { productTypesAnalytics, TODOrDefault } from '../../../analytics/util';
import { encode_deviceprint as encodeDeviceprint } from '../../../rsa-fingerprint';
import { retrieveFilterAndAssignAppID } from '../../shared/services/profile-service';

export const makeLoadProfileData = (): DataLoader =>
  async function loadProfileData(
    a: ApplicationState
  ): Promise<StateStoreOrVoid> {
    let existingProfileAppID = '';
    const userResults = await loadUser(a);

    const existingAccountAgreementsResults: any[] = await getUserExistingAgreements(
      userResults,
      [accountTypes.INDIVIDUAL, accountTypes.TOD]
    );

    if (
      userResults?.app?.customer?.primaryEmailAddress &&
      userResults?.authenticated
    ) {
      existingProfileAppID = await retrieveFilterAndAssignAppID(
        userResults?.app?.customer?.primaryEmailAddress,
        accountTypes.INDIVIDUAL
      );
    }

    return {
      ...(existingAccountAgreementsResults?.length && {
        existingAgreements: existingAccountAgreementsResults
      }),
      ...mergeTyped<StateStoreOrVoid>(userResults, {
        accountType: accountTypes.INDIVIDUAL,
        app: {
          profileApplicationID: existingProfileAppID
        }
      })
    };
  };

export const commitPersonalInformation = async (
  appState
): Promise<PartialStateStore> => {
  const {
    inputs,
    store: {
      amount,
      enabled,
      app: { customer }
    },
    chosen
  } = appState;

  const {
    ssn = '',
    firstName,
    lastName,
    middleName = '',
    dob,
    citizenship = ''
  } = inputs;

  let res;

  if (Array.isArray(chosen)) {
    res = commitNewInvestmentsBYOP(appState);
  } else {
    const taxID = enabled ? customer?.taxID : ssn.replace(/[^*0-9]/g, '');

    const enablementStatus = await getEnablementStatus(appState);

    res = merge(
      {
        amount,
        app: {
          customer: {
            name: {
              firstName,
              lastName,
              middleName
            },
            dob: formatDate(dob, 'MM/DD/YYYY', ''),
            citizenship:
              withDefault(inputs.citizenship, customer?.citizenship) || '',
            taxID,
            taxIDType: setTaxIDType(citizenship)
          }
        }
      },
      enablementStatus
    );
  }

  return res;
};

export const createIndividualApplication: DataLoader = async ({
  inputs,
  store,
  chosen
}): Promise<StateStoreOrVoid> => {
  if (
    ['byop', 'funding', 'info', 'beneficiaries', 'contact'].includes(chosen)
  ) {
    return { inReviewMode: true };
  }

  const { app, consumerName } = store;
  const { customer, termsIDs } = app;
  const { eligibleForTaxWithholding = false } = inputs;
  // use terms ids to create consent
  await commitConsent(termsIDs, customer);
  // terms ids should be an array of strings for service
  const stringTermsIDs = termsIDs?.map(id => id?.toString());
  // add rollover type and term IDs to account creation service payload
  const updatedAppState = {
    ...app,
    fundingMethodType: fundingTypes.FUND_LATER,
    eligibleForTaxWithholding,
    termsIDs: stringTermsIDs,
    deviceToken: encodeDeviceprint()
  };

  // get the service error code and tracking id on the initial submission request
  const {
    serviceErrorCode,
    trackingIdentifier
  } = await IndividualApplicationService(updatedAppState, consumerName);

  const isSubmissionTimedOut = serviceErrorCode === 504;

  if (isSubmissionTimedOut) {
    return {
      loadedData: {
        is504Timeout: true
      },
      app: {
        applicationStatus: applicationStatusTypes.COMPLETED
      }
    };
  }

  const submissionResult = await pollForSubmissionResponse(trackingIdentifier);
  return submissionResult;
};
// specifies loading message when creating account at end
createIndividualApplication.message = 'Creating your new account';
createIndividualApplication.secondaryMessage = secondaryCommitMessage;

export const updateAnalytics: DataLoader = async (
  { store },
  flowProps
): Promise<StateStoreOrVoid> => {
  analyticsDataLayer({
    accountType: TODOrDefault(store?.app?.beneficiaries, 'INDIVIDUAL'),
    productType: productTypesAnalytics.MUTUAL_FUND,
    sourceType: getAppSourceType(store?.consumerName),
    userType:
      store?.authenticated && !store?.loadedData?.newUserCreated
        ? 'Existing'
        : 'New',
    amount: store?.amount,
    isComplete: Boolean(flowProps?.analytics?.isComplete),
    clientTier: store?.summitData?.assetTierID?.toString()
  });
  dispatchPageLoad(flowProps?.analytics.pageName);
};
